/**
 * AlfeSync watcher — recursive file watcher with debounce and ignore support.
 *
 * Uses chokidar to watch the workspace root, debounces per-file changes
 * by 2 seconds, and emits batches of changed paths.
 */

import { watch } from "chokidar";
import { relative } from "node:path";
import { loadIgnorePatterns, shouldIgnore } from "./ignore.js";

export interface WatcherOptions {
  workspacePath: string;
  /** Debounce delay per file in milliseconds. Default: 2000 */
  debounceMs?: number;
  /** Callback invoked with batches of changed relative paths */
  onChanges: (paths: string[]) => void | Promise<void>;
}

/**
 * Start watching a workspace for file changes.
 *
 * Returns a cleanup function to stop watching.
 */
export async function startWatcher(
  options: WatcherOptions,
): Promise<() => Promise<void>> {
  const { workspacePath, debounceMs = 2000, onChanges } = options;
  const ignorePatterns = await loadIgnorePatterns(workspacePath);

  // Pending changes map: relativePath → timeout handle
  const pending = new Map<string, ReturnType<typeof setTimeout>>();
  // Batch accumulator for flushing
  let batchPaths = new Set<string>();
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleBatch() {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      if (batchPaths.size === 0) return;

      const paths = [...batchPaths];
      batchPaths = new Set();
      void onChanges(paths);
    }, debounceMs);
  }

  function handleChange(absolutePath: string) {
    const relativePath = relative(workspacePath, absolutePath);

    // Skip ignored files
    if (shouldIgnore(relativePath, ignorePatterns)) return;

    // Clear existing timer for this file
    const existingTimer = pending.get(relativePath);
    if (existingTimer) clearTimeout(existingTimer);

    // Debounce: wait before adding to batch
    const timer = setTimeout(() => {
      pending.delete(relativePath);
      batchPaths.add(relativePath);
      scheduleBatch();
    }, debounceMs);

    pending.set(relativePath, timer);
  }

  const watcher = watch(workspacePath, {
    persistent: true,
    ignoreInitial: true,
    followSymlinks: false,
    depth: undefined, // unlimited depth
    ignored: [
      "**/node_modules/**",
      "**/.alfesync/**",
      "**/.git/**",
      "**/.sst/**",
    ],
  });

  watcher.on("add", handleChange);
  watcher.on("change", handleChange);
  watcher.on("unlink", handleChange);

  // Return cleanup function
  return async () => {
    // Clear all pending timers
    for (const timer of pending.values()) {
      clearTimeout(timer);
    }
    pending.clear();

    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }

    await watcher.close();
  };
}
